import { useState } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { format } from 'date-fns'
import { useAdminAuditLog } from '@/hooks/useAdminAuditLog'
import type { AuditLogEntry } from '@/lib/api/admin-audit'
import { DataTable } from '@/components/shared/DataTable'
import { PageHeader } from '@/components/shared/PageHeader'
import { Badge } from '@/components/ui/badge'

const actionColors: Record<string, string> = {
  'user.tier_override': 'bg-blue-100 text-blue-800',
  'user.suspend': 'bg-red-100 text-red-800',
  'user.unsuspend': 'bg-green-100 text-green-800',
  'user.role_set': 'bg-purple-100 text-purple-800',
  'user.role_remove': 'bg-yellow-100 text-yellow-800',
  'user.internal_access_grant': 'bg-purple-100 text-purple-800',
  'user.internal_access_revoke': 'bg-yellow-100 text-yellow-800',
  'user.impersonate': 'bg-purple-100 text-purple-800',
  'user.is_test_set': 'bg-slate-100 text-slate-800',
  'user.is_test_unset': 'bg-slate-100 text-slate-800',
  'catalog.create': 'bg-green-100 text-green-800',
  'catalog.update': 'bg-blue-100 text-blue-800',
  'catalog.delete': 'bg-red-100 text-red-800',
  'layout.force_unpublish': 'bg-red-100 text-red-800',
  'layout.soft_delete': 'bg-red-100 text-red-800',
  'layout.restore': 'bg-green-100 text-green-800',
  'layout.export': 'bg-slate-100 text-slate-800',
  'location.export': 'bg-slate-100 text-slate-800',
}

const columns: ColumnDef<AuditLogEntry, unknown>[] = [
  {
    accessorKey: 'created_at',
    header: 'Timestamp',
    cell: ({ getValue }) => (
      <span className="text-sm">
        {format(new Date(getValue() as string), 'MMM d, HH:mm:ss')}
      </span>
    ),
  },
  {
    accessorKey: 'admin_id',
    header: 'Admin',
    cell: ({ getValue }) => (
      <code className="text-xs text-muted-foreground">{(getValue() as string).slice(0, 8)}...</code>
    ),
  },
  {
    accessorKey: 'action',
    header: 'Action',
    cell: ({ getValue }) => {
      const action = getValue() as string
      return (
        <Badge variant="secondary" className={actionColors[action] ?? ''}>
          {action}
        </Badge>
      )
    },
  },
  {
    accessorKey: 'target_type',
    header: 'Target',
    cell: ({ getValue }) => (
      <span className="text-sm">{getValue() as string}</span>
    ),
  },
  {
    accessorKey: 'target_id',
    header: 'Target ID',
    cell: ({ getValue }) => {
      const id = getValue() as string | null
      if (!id) return <span className="text-muted-foreground">—</span>
      return <code className="text-xs">{id.slice(0, 12)}...</code>
    },
  },
  {
    accessorKey: 'details',
    header: 'Details',
    cell: ({ getValue }) => {
      const details = getValue() as Record<string, unknown>
      const entries = Object.entries(details).filter(([, v]) => v != null && v !== '')
      if (entries.length === 0) return <span className="text-muted-foreground">—</span>
      return (
        <div className="max-w-xs text-xs text-muted-foreground">
          {entries.map(([k, v]) => (
            <span key={k} className="mr-2">
              <span className="font-medium">{k}:</span> {String(v)}
            </span>
          ))}
        </div>
      )
    },
  },
]

export function AuditLogPage() {
  const [page, setPage] = useState(0)
  const pageSize = 25
  const { data, isLoading } = useAdminAuditLog({ limit: pageSize, offset: page * pageSize })

  return (
    <div>
      <PageHeader
        title="Audit Log"
        description="Track all admin actions"
      />

      <DataTable
        columns={columns}
        data={data?.entries ?? []}
        isLoading={isLoading}
        emptyMessage="No audit log entries."
        pagination={{ pageIndex: page, pageSize }}
        onPaginationChange={(updater) => {
          const next = typeof updater === 'function'
            ? updater({ pageIndex: page, pageSize })
            : updater
          setPage(next.pageIndex)
        }}
        pageCount={Math.ceil((data?.total ?? 0) / pageSize)}
      />
    </div>
  )
}
